/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
        Module No. 17, SBC, Thejaswini Building
        Phase 1, Technopark, Thiruvananthapuram - 695581, 
        Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import React, { useState, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { useTranslation, Trans } from 'react-i18next';
import { rawMaterials } from '../../../service/rawMaterials';
import { userService } from '../../../service/plateServices';
import { MLTBioBurdenServices } from '../../../service/MLTBioBurden';
import { MediaBatchPreparationInitServices } from '../../../service/mediaBatchPrepInitServices';
import { EsignModal } from '../../../widgets/alertModal';

export interface AssignedPlate {
  serialNo: string;
  plateId: string;
  mediaType: string;
  plateSize: string;
  lotNo: string;
  expiryDate: string;
  status: 'Available' | 'Assigned' | 'QC Released';
  intendedTarget: string;
  isExpired: boolean;
  isCompatible: boolean;
}

interface MLTBioburdenInitiateProps {
  onBack: () => void;
  onViewInitiatedTest: (id: string) => void;
  // onNavigate: () => void;
  taskData: any;
}

// TODO: confirm these ids against the master tables / backend.
const TEST_METHOD_ID_MAP: Record<'Plate Count' | 'Filtration', number> = {
  Filtration: 1,
  'Plate Count': 2
};
const INOCULATION_TYPE_ID_MAP: Record<string, number> = {
  'Pour Plate Method': 1,
  'Spread Plate Method': 2
};
const UOM_ID_MAP: Record<string, number> = {
  g: 1,
  mL: 2,
  mg: 3,
  kg: 4,
  vials: 5
};

// "2026-10-14T07:45" -> "2026-10-14 07:45:00"
const toApiDateTime = (value: string): string => {
  const s = (value || '').replace('T', ' ');
  return s.length === 16 ? `${s}:00` : s;
};

// any parsable date -> "YYYY-MM-DD" (local), falling back to the date part of `fallback`
const toApiDate = (value?: string | null, fallback?: string): string => {
  const d = value ? new Date(value) : null;
  if (d && !isNaN(d.getTime())) {
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${d.getFullYear()}-${mm}-${dd}`;
  }
  return (fallback || '').slice(0, 10);
};

export const MLTBioburdenInitiate: React.FC<MLTBioburdenInitiateProps> = ({ onBack, onViewInitiatedTest, taskData }) => {
  const { t } = useTranslation();

  // Normalize task information from props or fallback to realistic default
  const task = useMemo(() => {
    return {
      taskId: taskData?.task_id,
      testId: taskData?.id ,
      testMethod: (taskData?.testMethod || 'Filtration') as 'Plate Count' | 'Filtration',
      generationDate: taskData?.scheduledDate || '14-Oct-2026 08:30:00',
      assignedOperator: taskData?.assignedTo || 'Dr. Sarah Jenkins (Senior QC Microbiologist)',
      productionSchedule: taskData?.productionSchedule || 'SCH-2026-0952',
      scheduleLine: taskData?.scheduleLine || 'Line 2 - Aseptic Filling Suite',
      productName: taskData?.productName || 'Ceftriaxone Sodium Injection 1g',
      productCode: taskData?.productCode || 'PRD-CFX-101',
      batchNo: taskData?.batchNo || 'CFX-261014',
      taskStatus: 'Pending Initiation',
      requiredPlateCount: taskData?.testMethod === 'Plate Count' ? 4 : 2
    };
  }, [taskData]);

  // Method state (derived from task, with fallback toggle)
  const [testMethod, setTestMethod] = useState<'Plate Count' | 'Filtration'>(task.testMethod);

  // Common Sample Details
  // sampleType / sampleSubtype hold the selected master-table IDs (as strings)
  const [sampleType, setSampleType] = useState<string>('');
  const [sampleSubtype, setSampleSubtype] = useState<string>('');
  const [sampleId, setSampleId] = useState<string>(taskData?.sampleId || 'SMP-2026-FP-0952-01');
  // const [sampleQuantity, setSampleQuantity] = useState<string>('10.0');
  const [uom, setUom] = useState<string>('g');
  const [sampledDateTime, setSampledDateTime] = useState<string>('2026-10-14T07:45');
  const [remarks, setRemarks] = useState<string>('');

    // Sample Details Quantities (Material Accounting Fields)
  const [quantitySampled, setQuantitySampled] = useState<string>(taskData?.quantitySampled ? String(parseFloat(taskData.quantitySampled)) : '100');
  const [quantityUsedForTest, setQuantityUsedForTest] = useState<string>(taskData?.quantityUsedForTest ? String(parseFloat(taskData.quantityUsedForTest)) : '10');
  const [retentionSample, setRetentionSample] = useState<string>(taskData?.retentionQuantity ? String(parseFloat(taskData.retentionQuantity)) : '20');
  const [quantityDestroyed, setQuantityDestroyed] = useState<string>(taskData?.quantityDestroyed ? String(parseFloat(taskData.quantityDestroyed)) : '5');

  // Autocalculate Remaining Quantity: Sampled - (Used + Retention + Destroyed)
  const remainingQuantity = useMemo(() => {
    const sampled = parseFloat(quantitySampled) || 0;
    const used = parseFloat(quantityUsedForTest) || 0;
    const retention = parseFloat(retentionSample) || 0;
    const destroyed = parseFloat(quantityDestroyed) || 0;
    const rem = sampled - (used + retention + destroyed);
    if (isNaN(rem)) return '0';
    return Number.isInteger(rem) ? rem.toString() : rem.toFixed(2);
  }, [quantitySampled, quantityUsedForTest, retentionSample, quantityDestroyed]);

  // Production Batch Number (free text field, defaults to schedule batch but fully editable)
  const [productionBatchNo, setProductionBatchNo] = useState<string>(task.batchNo || '');

  // Conditional: Raw Material specific fields (mandatory when isRawMaterial)
  const [materialName, setMaterialName] = useState<string>('');
  const [materialBatchLot, setMaterialBatchLot] = useState<string>('');

  // Method Specific: Plate Count parameters
  const [pcPretreatment, setPcPretreatment] = useState<string>('');
  const [pcDilutionRequired, setPcDilutionRequired] = useState<'Yes' | 'No'>('Yes');
  const [pcDilutionFactor, setPcDilutionFactor] = useState<string>('');
  const [pcNeutralizer, setPcNeutralizer] = useState<string>('');
  const [pcInoculationType, setPcInoculationType] = useState<string>('Pour Plate Method');
  const [pcVolumePlated, setPcVolumePlated] = useState<string>('1.0');
  const [pcUom, setPcUom] = useState<string>('mL');

  // Method Specific: Filtration parameters
  const [filtPretreatment, setFiltPretreatment] = useState<string>('');
  const [filtType, setFiltType] = useState<string>('');
  const [filtPoreSize, setFiltPoreSize] = useState<string>('');
  const [filtMembraneDiameter, setFiltMembraneDiameter] = useState<string>('');
  const [filtVolumeFiltered, setFiltVolumeFiltered] = useState<string>('100.0');
  const [filtUom, setFiltUom] = useState<string>('mL');
  const [filtNumFilters, setFiltNumFilters] = useState<string>('2');
  const [filtNeutralizer, setFiltNeutralizer] = useState<string>('');
  const [plateAssesmentData, setPlateAssesmentData] = useState<Array<any>>([]);


  // UI States
  const [showValidationSummary, setShowValidationSummary] = useState(false);
  const [showInitiateModal, setShowInitiateModal] = useState(false);
  const [toastMessage, setToastMessage] = useState<{ title: string; desc: string; type: 'success' | 'info' | 'error' } | null>(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [operatorConfirmation, setOperatorConfirmation] = useState(false);
  const [eSigPassword, setESigPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [rawMaterialType, setRawMaterialType] = useState<Array<any>>([]);
  const [rawMaterialSub, setRawMaterialSub] = useState<Array<any>>([]);
  const [preTreatmentList, setPreTreatmentList] = useState<Array<any>>([]);
  const [filterTypeList, setFilterTypeList] = useState<Array<any>>([]);
  const [poreSizeList, setPoreSizeList] = useState<Array<any>>([]);
  const [membraneDiameterList, setMembraneDiameterList] = useState<Array<any>>([]);
  const [dilutionfactorList, setDilutionfactorList] = useState<Array<any>>([]);
  const [neutralizerList, setNeutralizerList] = useState<Array<any>>([]);
  const [userId, setUserId] = useState<number>(0);
  const [showEsignAlert, setShowEsignAlert] = useState(false);

  // sampleType now stores an id, so derive the display name + type flags from the loaded list
  const sampleTypeName = useMemo(
    () => rawMaterialType.find((x: any) => String(x.id) === String(sampleType))?.material_type || '',
    [rawMaterialType, sampleType]
  );
  const isFinishedProduct = sampleTypeName.trim().toLowerCase() === 'finished product';
  const isRawMaterial = sampleTypeName.trim().toLowerCase() === 'raw material';

  const formatExpiryDate = (date?: string | null): string => {
    if (!date) return '-';
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    return d
      .toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      .replace(/ /g, '-'); // 30-Nov-2026
  };

  const mapApiPlateToAssignedPlate = (plate: any): AssignedPlate => {
    const expiry = plate.expiry_date ? new Date(plate.expiry_date) : null;
    const isExpired = expiry ? expiry.getTime() < Date.now() : false;

    return {
      serialNo: plate.serial_number ?? '-',
      plateId: plate.plate_id ?? '-',
      mediaType: plate.plate_type ?? '-',        // API returns null for now
      plateSize: '-',                            // not in API
      lotNo: plate.lot_code ?? '-',
      expiryDate: formatExpiryDate(plate.expiry_date),
      status: plate.status === 'active' ? 'QC Released' : plate.status ?? '-',
      intendedTarget: plate.location_name ?? '-', // not in API; adjust when available
      isExpired,
      isCompatible: !isExpired
    };
  };

  const preAssignedPlates = useMemo<AssignedPlate[]>(
    () => plateAssesmentData.map(mapApiPlateToAssignedPlate),
    [plateAssesmentData]
  );

  // Handle sample type changes cleanly
  const handleSampleTypeChange = (newType: string) => {
    setSampleType(newType);
    setSampleSubtype('');
    setRawMaterialSub([]);
    setShowValidationSummary(false);
    getRawMaterialSubCtegory(newType);
  };

  // Check validation rules
  const validationErrors = useMemo(() => {
    const errors: string[] = [];

    // Mandatory Sample Type & Subtype
    if (!sampleType) {
      errors.push(t("mltBioburdenInitiate.validation.sampleTypeMandatory"));
    }
    if (!sampleSubtype) {
      errors.push(t("mltBioburdenInitiate.validation.sampleSubtypeMandatory"));
    }

    // Sample ID
    if (!sampleId.trim()) {
      errors.push(t("mltBioburdenInitiate.validation.sampleIdMandatory"));
    }

    // Quantity & UOM
    // const qtyNum = parseFloat(sampleQuantity);
    // if (!sampleQuantity || isNaN(qtyNum) || qtyNum <= 0) {
    //   errors.push(t("mltBioburdenInitiate.validation.quantityInvalid"));
    // }
    // if (!uom) {
    //   errors.push(t("mltBioburdenInitiate.validation.uomMandatory"));
    // }

    // Sample Quantities & Material Accounting Validation
    const sampledNum = parseFloat(quantitySampled);
    if (!quantitySampled || isNaN(sampledNum) || sampledNum <= 0) {
      errors.push('Quantity Sampled must be a valid number greater than zero.');
    }
    const usedNum = parseFloat(quantityUsedForTest);
    if (!quantityUsedForTest || isNaN(usedNum) || usedNum <= 0) {
      errors.push('Quantity Used for Test must be a valid number greater than zero.');
    }
    const remNum = parseFloat(remainingQuantity);
    if (remNum < 0) {
      errors.push('Total accounted quantity (Used + Retention + Destroyed) exceeds Total Quantity Sampled.');
    }
    if (!uom) {
      errors.push('Unit of Measure is mandatory.');
    }

    // Sampled Date & Time
    if (!sampledDateTime) {
      errors.push(t("mltBioburdenInitiate.validation.sampledDateTimeMandatory"));
    }

    // Conditional: Finished Product validation
    if (isFinishedProduct) {
      if (!task.productionSchedule) {
        errors.push(t("mltBioburdenInitiate.validation.noScheduleMapping"));
      }
      if (!task.productName) {
        errors.push(t("mltBioburdenInitiate.validation.noProductName"));
      }
      if (!productionBatchNo.trim()) {
        errors.push(t("mltBioburdenInitiate.validation.batchNoMandatory"));
      }
    }

    // Conditional: Raw Material validation
    if (isRawMaterial) {
      if (!materialName.trim()) {
        errors.push(t("mltBioburdenInitiate.validation.materialNameMandatory"));
      }
      if (!materialBatchLot.trim()) {
        errors.push(t("mltBioburdenInitiate.validation.materialLotMandatory"));
      }
    }

    // Plate Assignment validation
    // const requiredCount = testMethod === 'Plate Count' ? 4 : 2;
    // if (preAssignedPlates.length !== requiredCount) {
    //   errors.push(t("mltBioburdenInitiate.validation.plateAssignmentIncomplete", { assigned: preAssignedPlates.length, required: requiredCount }));
    // }

    const serials = preAssignedPlates.map(p => p.serialNo);
    const hasDuplicates = new Set(serials).size !== serials.length;
    if (hasDuplicates) {
      errors.push(t("mltBioburdenInitiate.validation.duplicatePlates"));
    }

    const hasExpiredPlates = preAssignedPlates.some(p => p.isExpired);
    if (hasExpiredPlates) {
      errors.push(t("mltBioburdenInitiate.validation.expiredPlates"));
    }

    const hasIncompatiblePlates = preAssignedPlates.some(p => !p.isCompatible);
    if (hasIncompatiblePlates) {
      errors.push(t("mltBioburdenInitiate.validation.incompatiblePlates"));
    }

    // Method specific validations
    if (testMethod === 'Plate Count') {
      const volNum = parseFloat(pcVolumePlated);
      if (isNaN(volNum) || volNum <= 0) {
        errors.push(t("mltBioburdenInitiate.validation.volumePlatedInvalid"));
      }
      if (pcDilutionRequired === 'Yes' && !pcDilutionFactor) {
        errors.push(t("mltBioburdenInitiate.validation.dilutionFactorRequired"));
      }
      if (!pcPretreatment) {
        errors.push('Pretreatment / Sample Preparation is mandatory.');
      }
      if (!pcNeutralizer) {
        errors.push('Neutralizer is mandatory.');
      }
    } else {
      const filtVol = parseFloat(filtVolumeFiltered);
      if (isNaN(filtVol) || filtVol <= 0) {
        errors.push(t("mltBioburdenInitiate.validation.volumeFilteredInvalid"));
      }
      const numFilt = parseInt(filtNumFilters, 10);
      if (isNaN(numFilt) || numFilt < 1) {
        errors.push(t("mltBioburdenInitiate.validation.numFiltersInvalid"));
      }
      if (!filtPretreatment) {
        errors.push('Pretreatment / Sample Preparation is mandatory.');
      }
      if (!filtType) {
        errors.push('Filter Type is mandatory.');
      }
      if (!filtPoreSize) {
        errors.push('Pore Size is mandatory.');
      }
      if (!filtMembraneDiameter) {
        errors.push('Membrane Diameter is mandatory.');
      }
      if (!filtNeutralizer) {
        errors.push('Neutralizer / Rinse Fluid is mandatory.');
      }
    }

    return errors;
  }, [
    sampleType,
    sampleSubtype,
    sampleId,
    // sampleQuantity,
    quantitySampled,
    quantityUsedForTest,
    retentionSample,
    quantityDestroyed,
    remainingQuantity,
    uom,
    sampledDateTime,
    task,
    productionBatchNo,
    materialName,
    materialBatchLot,
    testMethod,
    preAssignedPlates,
    pcVolumePlated,
    pcDilutionRequired,
    pcDilutionFactor,
    filtVolumeFiltered,
    filtNumFilters,
    isFinishedProduct,
    isRawMaterial,
    pcPretreatment,
    pcNeutralizer,
    filtPretreatment,
    filtType,
    filtPoreSize,
    filtMembraneDiameter,
    filtNeutralizer,
    t
  ]);

  const isFormValid = validationErrors.length === 0;


  // Handle Click Initiate
  const handleInitiateClick = () => {
    if (!isFormValid) {
      setShowValidationSummary(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    setShowEsignAlert(true)
  };

  // Build the payload expected by the MLT Bioburden initiate API
  const buildInitiatePayload = () => {
    const isFiltration = testMethod === 'Filtration';

    const payload: Record<string, any> = {
      mlt_test_id: task.taskId,
      user_id: userId,
      plate_ids: plateAssesmentData.map((p: any) => p.plate_id).join(','),
      test_method_id: TEST_METHOD_ID_MAP[testMethod],
      sample_type_id: Number(sampleType),
      sample_subtype_id: Number(sampleSubtype),
      sample_id: sampleId.trim(),
      sampled_at: toApiDateTime(sampledDateTime),
      schedule_date: toApiDate(taskData?.scheduledDate, sampledDateTime),
      // assigned_to: assignedToId,
      material_name: materialName.trim(),
      material_batch_number: materialBatchLot.trim(),
      technician_remarks: remarks.trim(),
      pretreatment_id: Number(isFiltration ? filtPretreatment : pcPretreatment),
      neutralizer_solution_id: Number(isFiltration ? filtNeutralizer : pcNeutralizer),
      quantity_sampled: parseFloat(quantitySampled),
      quantity_used_for_test: parseFloat(quantityUsedForTest),
      retention_sample: parseFloat(retentionSample) || 0,
      quantity_destroyed: parseFloat(quantityDestroyed) || 0,
      quantity_uom_id: UOM_ID_MAP[uom],
      production_batch_number: productionBatchNo
    };

    if (isFiltration) {
      payload.filter_type_id = Number(filtType);
      payload.pore_size_id = Number(filtPoreSize);
      payload.membrane_diameter_id = Number(filtMembraneDiameter);
      payload.volume_filtered = parseFloat(filtVolumeFiltered);
      payload.number_of_filters = parseInt(filtNumFilters, 10);
      payload.volume_uom = filtUom;
    } else {
      // Plate Count specific fields
      const dilutionRequired = pcDilutionRequired === 'Yes';
      payload.dilution_required = dilutionRequired;
      payload.dilution_factor_id = dilutionRequired ? Number(pcDilutionFactor) : null;
      payload.inoculation_type_id = INOCULATION_TYPE_ID_MAP[pcInoculationType];
      payload.volume_plated = parseFloat(pcVolumePlated);
      payload.volume_uom = pcUom;
    }
    // NOTE: Plate Count specific fields (dilution required / factor, inoculation type,
    // volume plated + uom) are not part of the agreed payload yet - add them here once
    // the backend field names are confirmed.

    return payload;
  };

  const showErrorToast = (desc: string) => {
    setToastMessage({
      title: t("mltBioburdenInitiate.messages.initiationFailedTitle", { defaultValue: 'Initiation Failed' }),
      desc,
      type: 'error'
    });
    setTimeout(() => setToastMessage(null), 6000);
  };

  // Confirm Final Initiation
  const handleConfirmInitiation = async () => {
    if (isSubmitting) return;

    const dataToSend = buildInitiatePayload();

    // Never post NaN / missing ids (they would be serialised as null)
    // const missing = Object.entries(dataToSend)
    //   .filter(([key, value]) =>
    //     (key.endsWith('_id') || key === 'assigned_to') && key !== 'sample_id' &&
    //     (typeof value !== 'number' || !Number.isFinite(value))
    //   )
    //   .map(([key]) => key);
    // if (missing.length > 0) {
    //   setShowInitiateModal(false);
    //   showErrorToast(`Missing or invalid value for: ${missing.join(', ')}`);
    //   return;
    // }

    try {
      setIsSubmitting(true);
      const response = await MLTBioBurdenServices.initiateMLTBioBurdenTest(dataToSend);

      if (response?.status && (response?.statusCode === 200 || response?.statusCode === 201)) {
        setShowInitiateModal(false);
        setIsSubmitted(true);
        setToastMessage({
          title: t("mltBioburdenInitiate.messages.initiatedTitle"),
          desc: t("mltBioburdenInitiate.messages.initiatedDesc", { testId: task.testId }),
          type: 'success'
        });
      } else {
        setShowInitiateModal(false);
        showErrorToast(
          response?.message ||
          t("mltBioburdenInitiate.messages.initiationFailedDesc", { defaultValue: 'Unable to initiate the test. Please try again.' })
        );
      }
    } catch (error: any) {
      console.error('MLT Bioburden initiate error', error);
      setShowInitiateModal(false);
      showErrorToast(
        error?.response?.data?.message ||
        error?.message ||
        t("mltBioburdenInitiate.messages.initiationFailedDesc", { defaultValue: 'Unable to initiate the test. Please try again.' })
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  //dropdown to show the raw material type (sample type)
  const getRawMaterialType = async () => {
    try {
      const response = await userService.getRawMaterialType();
      if (response.status && response.statusCode === 200 && Array.isArray(response.data) && response.data.length > 0) {
        const rowData = response.data
          .filter((element: any) => element.status === "active")
          .map((element: any) => ({
            id: element.id,
            material_type: element.material_type,
            status: element.status,
          }));
        setRawMaterialType(rowData);
        if (rowData.length > 0) {
          const preferred =
            rowData.find((r: any) => String(r.id) === String(taskData?.sampleTypeId) || r.material_type === taskData?.sampleType) ??
            rowData[0];
          setSampleType(String(preferred.id));
          getRawMaterialSubCtegory(String(preferred.id));
        }
      }
    } catch (error) {
      console.error('Sample type response', error);
    }
  }

  //dropdown to show the raw material sub category (sample subtype)
  const getRawMaterialSubCtegory = async (material_id: string) => {
    try {
      const response = await userService.getRawMaterialSubCtegory(material_id);
      const rowData =
        response.status && response.statusCode === 200 && Array.isArray(response.data)
          ? response.data
              .filter((element: any) => element.status === "active")
              .map((element: any) => ({
                id: element.id,
                material_type: element.name,
                status: element.status,
              }))
          : [];
      setRawMaterialSub(rowData);
      const preferred = rowData.find((r: any) => String(r.id) === String(taskData?.sampleSubtypeId)) ?? rowData[0];
      setSampleSubtype(preferred ? String(preferred.id) : '');
    } catch (error) {
      console.error('Sample subtype response', error);
      setRawMaterialSub([]);
      setSampleSubtype('');
    }
  }

  //dropdown to show pre treatment list
  const getPreTreatmentList = async () => {
    try {
      const response = await MLTBioBurdenServices.getPreTreatmentList();
      if (response.status && response.statusCode === 200) {
        try {
          if (Array.isArray(response.data) && response.data?.length > 0) {
            let rowData: any = response.data.map((element: any, index: number) => {
              return {
                id: element.id,
                pretreatment_name: element.pretreatment_name,
                status: element.status,
              };
            });
            setPreTreatmentList(rowData);
            setPcPretreatment((prev) => prev || String(rowData[0].id));
            setFiltPretreatment((prev) => prev || String(rowData[0].id));
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.error('Plate response', error);
    }
  }

  //dropdown to show filter type list
  const getFilterTypeList = async () => {
    try {
      const response = await MLTBioBurdenServices.getFilterTypeList();
      if (response.status && response.statusCode === 200) {
        try {
          if (Array.isArray(response.data) && response.data?.length > 0) {
            let rowData: any = response.data.map((element: any, index: number) => {
              return {
                id: element.id,
                filter_type_name: element.filter_type_name,
                status: element.status,
              };
            });
            setFilterTypeList(rowData);
            setFiltType((prev) => prev || String(rowData[0].id));
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.error('Plate response', error);
    }
  }

  //dropdown to show pore size list
  const getPoreSizeList = async () => {
    try {
      const response = await MLTBioBurdenServices.getPoreSizeList();
      if (response.status && response.statusCode === 200) {
        try {
          if (Array.isArray(response.data) && response.data?.length > 0) {
            let rowData: any = response.data.map((element: any, index: number) => {
              return {
                id: element.id,
                pore_size: element.pore_size,
                status: element.status,
              };
            });
            setPoreSizeList(rowData);
            setFiltPoreSize((prev) => prev || String(rowData[0].id));
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.error('Plate response', error);
    }
  }

  //dropdown to show membrane diameter list
  const getMembraneDiameterList = async () => {
    try {
      const response = await MLTBioBurdenServices.getMembraneDiameterList();
      if (response.status && response.statusCode === 200) {
        try {
          if (Array.isArray(response.data) && response.data?.length > 0) {
            let rowData: any = response.data.map((element: any, index: number) => {
              return {
                id: element.id,
                membrane_diameter: element.membrane_diameter,
                status: element.status,
              };
            });
            setMembraneDiameterList(rowData);
            setFiltMembraneDiameter((prev) => prev || String(rowData[0].id));
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.error('Plate response', error);
    }
  }

  //api call for dilution factor listing
  const getDialutionFactorList = async () => {
    try {
      const response = await MLTBioBurdenServices.getDilutionFactorList();
      if (response.status && response.statusCode === 200) {
        try {
          if (Array.isArray(response.data) && response.data?.length > 0) {
            let rowData: any = response.data.map((element: any, index: number) => {
              return {
                id: element.id,
                df: element.df,
                status: element.status,
              };
            });
            setDilutionfactorList(rowData);
            setPcDilutionFactor((prev) => prev || String(rowData[0].id));
          }
        } catch (error) {
          console.log(error);
        }
      }
    } catch (error) {
      console.error('Plate response', error);
    }
  }

  // list Neutralizer
  const fetchNeutralizer = async () => {
    try {
      const response = await MediaBatchPreparationInitServices.getMediaSolutionBatches({
        page: 1, size: 50, sort: "id", order: "desc", status: "Released", category: "Neutralizer",
      });
      if (Array.isArray(response.data) && response.data?.length > 0) {
        let rowData: any = response.data.map((element: any, index: number) => {
          return {
            id: element.id,
            solution_name: element.solution_name,
            status: element.status,
          };
        });
        setNeutralizerList(rowData);
        setPcNeutralizer((prev) => prev || String(rowData[0].id));
        setFiltNeutralizer((prev) => prev || String(rowData[0].id));
      }
    } catch (err) {
      console.error("Failed to fetch batches:", err);
    }
  };

  //api call for MLTBioburden task details
  const getMLTBioburdenTaskDetails = async (testId: string) => {
    try {
      const response = await MLTBioBurdenServices.getMLTBioBurdenTaskDetails(testId);

      if (response?.status && response?.statusCode === 200 && response?.data) {
        setPlateAssesmentData(response.data.plates);
      } else {
        setPlateAssesmentData([]);
      }
    } catch (error) {
      console.error('MLT Bioburden task details error', error);
      setPlateAssesmentData([]);
    } finally {
    }
  };


  useEffect(() => {
    var storedData = localStorage.getItem("userDetails");
    if (storedData) {
      var storedObject = JSON.parse(storedData);
      setUserId(storedObject[0].id);
    }

    getRawMaterialType();
    getPreTreatmentList();
    getFilterTypeList();
    getPoreSizeList();
    getMembraneDiameterList();
    getDialutionFactorList();
    fetchNeutralizer();
  }, [])

  useEffect(() => {
    getMLTBioburdenTaskDetails(task.testId);
  }, [task.testId]);

  return (
    <div className="min-h-screen bg-[#F8FAFC] text-slate-800 pb-16 media-prep-scope">

      {/* Toast Notification */}
      <AnimatePresence>
        {toastMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className={`fixed top-4 right-6 z-50 flex items-start gap-3 p-4 rounded-xl border shadow-xl max-w-md ${toastMessage.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                : toastMessage.type === 'error'
                  ? 'bg-rose-50 border-rose-200 text-rose-900'
                  : 'bg-blue-50 border-blue-200 text-blue-900'
              }`}
          >
            <span className="material-symbols-outlined text-xl">
              {toastMessage.type === 'success' ? 'check_circle' : toastMessage.type === 'error' ? 'error' : 'info'}
            </span>
            <div className="flex-1">
              <h4 className="text-xs font-black uppercase tracking-wider">{toastMessage.title}</h4>
              <p className="text-xs mt-0.5 text-slate-600 font-medium">{toastMessage.desc}</p>
            </div>
            <button
              onClick={() => setToastMessage(null)}
              className="text-slate-400 hover:text-slate-600 text-sm"
            >
              <span className="material-symbols-outlined text-base">close</span>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Professional Header Bar */}
      <header className="bg-white border-b border-slate-200 shadow-sm sticky top-0 z-30">
        <div className="max-w-[1720px] mx-auto px-6 py-3.5">
          {/* Breadcrumb & Top Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              {/* Clean Breadcrumb */}
              <nav className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-400 uppercase tracking-wider">
                <span className="hover:text-slate-600 cursor-pointer" >{t("mltBioburdenInitiate.breadcrumb.home")}</span>
                <span>/</span>
                <span className="hover:text-slate-600 cursor-pointer" >{t("mltBioburdenInitiate.breadcrumb.mlt")}</span>
                <span>/</span>
                <span className="hover:text-slate-600 cursor-pointer" >{t("mltBioburdenInitiate.breadcrumb.taskList")}</span>
                <span>/</span>
                <span className="text-blue-600 font-bold">{t("mltBioburdenInitiate.breadcrumb.initiate")}</span>
              </nav>

              <div className="flex items-center gap-3 mt-1">
                <h1 className="text-xl font-black text-[#0F2942] tracking-tight">
                  {t("mltBioburdenInitiate.header.title")}
                </h1>
                <span className="px-2.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded text-[11px] font-black uppercase tracking-widest flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse"></span>
                  {task.taskStatus}
                </span>
                <span className="text-xs text-slate-400 font-medium hidden sm:inline">|</span>
                <span className="text-xs text-slate-500 font-medium hidden sm:inline">
                  {t("mltBioburdenInitiate.header.compliance")}
                </span>
              </div>
            </div>

            {/* Header Right Actions & Badges */}
            <div className="flex items-center gap-3">
              <button
                onClick={onBack}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-xs font-bold transition-all flex items-center gap-1.5 border border-slate-200"
              >
                <span className="material-symbols-outlined text-base">arrow_back</span>
                {t("mltBioburdenInitiate.header.backToTaskList")}
              </button>
            </div>
          </div>

          {/* Header Metadata Chips / Quick Facts */}
          <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap items-center justify-between gap-4 text-xs">
            <div className="flex flex-wrap items-center gap-5">
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenInitiate.common.testIdLabel")}</span>
                <span className="font-mono font-bold text-blue-700 bg-blue-50 px-2 py-0.5 rounded border border-blue-100">
                  {task.testId}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenInitiate.common.taskIdLabel")}</span>
                <span className="font-mono font-bold text-slate-700 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                  {task.taskId}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenInitiate.header.assignedOperator")}</span>
                <span className="font-bold text-slate-800 flex items-center gap-1">
                  <span className="material-symbols-outlined text-sm text-blue-500">badge</span>
                  {task.assignedOperator}
                </span>
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenInitiate.header.method")}</span>
                <span className="font-bold text-indigo-700 bg-indigo-50 px-2 py-0.5 rounded border border-indigo-100">
                  {testMethod}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t("mltBioburdenInitiate.header.platesRequired")}</span>
              <span className="font-bold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded border border-emerald-100">
                {t("mltBioburdenInitiate.common.platesVerified", { total: preAssignedPlates.length })}
              </span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content Container (Max width 1720px for desktop 1920x1080 display) */}
      <main className="max-w-[1720px] mx-auto px-6 pt-6 space-y-6">
        {/* Post-Initiation Success Card if already submitted */}
        {isSubmitted && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-6 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-md">
                <span className="material-symbols-outlined text-2xl">task_alt</span>
              </div>
              <div>
                <h3 className="text-base font-black text-emerald-950 uppercase tracking-wide">
                  {t("mltBioburdenInitiate.success.title")}
                </h3>
                <p className="text-xs text-emerald-800 mt-1">
                  <Trans
                    i18nKey="mltBioburdenInitiate.success.description"
                    values={{ taskId: task.taskId, testId: task.testId }}
                    components={{ mono: <strong className="font-mono" /> }}
                  />
                </p>
                <div className="flex items-center gap-4 mt-2 text-xs text-emerald-700 font-semibold">
                  <span>{t("mltBioburdenInitiate.success.operator", { operator: task.assignedOperator })}</span>
                  <span>•</span>
                  <span>{t("mltBioburdenInitiate.success.timestamp", { timestamp: new Date().toLocaleString() })}</span>
                  <span>•</span>
                  <span>{t("mltBioburdenInitiate.success.auditLogId", { id: Math.floor(100000 + Math.random() * 900000) })}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => onViewInitiatedTest(task.testId)}
                className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs font-black uppercase tracking-wider shadow-sm transition-all flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined text-base">visibility</span>
                {t("mltBioburdenInitiate.success.viewInitiatedTest")}
              </button>
              <button
                onClick={onBack}
                className="px-5 py-2.5 bg-white border border-emerald-300 hover:bg-emerald-100 text-emerald-800 rounded-lg text-xs font-black uppercase tracking-wider transition-all"
              >
                {t("mltBioburdenInitiate.success.returnToTaskList")}
              </button>
            </div>
          </div>
        )}

        {/* Validation Failure Summary Banner */}
        {showValidationSummary && validationErrors.length > 0 && (
          <div className="bg-rose-50 border-2 border-rose-300 rounded-2xl p-5 shadow-sm">
            <div className="flex items-start gap-3">
              <span className="material-symbols-outlined text-rose-600 text-2xl shrink-0 mt-0.5">
                report
              </span>
              <div className="flex-1">
                <h3 className="text-sm font-black text-rose-900 uppercase tracking-wider">
                  {t("mltBioburdenInitiate.validationBanner.title")}
                </h3>
                <p className="text-xs text-rose-700 mt-0.5">
                  {t("mltBioburdenInitiate.validationBanner.description")}
                </p>
                <ul className="mt-3 space-y-1.5 text-xs text-rose-800 font-medium">
                  {validationErrors.map((err, idx) => (
                    <li key={idx} className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-rose-500 shrink-0"></span>
                      <span>{err}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <button
                onClick={() => setShowValidationSummary(false)}
                className="text-rose-400 hover:text-rose-600 text-sm"
              >
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
          </div>
        )}

        {/* SECTION 1: Task Details – Read-only Section */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0F2942] text-white px-6 py-3.5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-md bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-black border border-blue-400/30">
                1
              </span>
              <h2 className="text-xs font-black tracking-widest uppercase">
                {t("mltBioburdenInitiate.taskDetails.title")}
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <span className="material-symbols-outlined text-xs text-emerald-400">verified</span>
              <span className="text-[11px] font-bold text-emerald-400 uppercase tracking-widest">
                {t("mltBioburdenInitiate.taskDetails.systemPreValidated")}
              </span>
            </div>
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {/* Task ID */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.taskId")}
                </span>
                <div className="font-mono font-bold text-slate-800 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-slate-400 text-base">assignment</span>
                  {task.taskId}
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">{t("mltBioburdenInitiate.taskDetails.taskIdHint")}</span>
              </div>

              {/* Test ID */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.testId")}
                </span>
                <div className="font-mono font-bold text-blue-700 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-blue-500 text-base">biotech</span>
                  {task.testId}
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">{t("mltBioburdenInitiate.taskDetails.testIdHint")}</span>
              </div>

              {/* Test Method */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.testMethod")}
                </span>
                <div className="font-bold text-slate-900 text-sm mt-1 flex items-center justify-between">
                  <span>{testMethod}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setTestMethod('Filtration')}
                      className={`px-2 py-0.5 rounded text-[10px] font-black uppercase transition-all ${testMethod === 'Filtration'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
                        }`}
                      title={t("mltBioburdenInitiate.taskDetails.switchToFiltration")}
                    >
                      {t("mltBioburdenInitiate.taskDetails.filtration")}
                    </button>
                    <button
                      type="button"
                      onClick={() => setTestMethod('Plate Count')}
                      className={`px-2 py-0.5 rounded text-[10px] font-black uppercase transition-all ${testMethod === 'Plate Count'
                          ? 'bg-blue-600 text-white'
                          : 'bg-white text-slate-500 border border-slate-200 hover:bg-slate-100'
                        }`}
                      title={t("mltBioburdenInitiate.taskDetails.switchToPlateCount")}
                    >
                      {t("mltBioburdenInitiate.taskDetails.plateCount")}
                    </button>
                  </div>
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">
                  {testMethod === 'Filtration' ? t("mltBioburdenInitiate.taskDetails.filtrationHint") : t("mltBioburdenInitiate.taskDetails.plateCountHint")}
                </span>
              </div>

              {/* Task Generation Date */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.generationDate")}
                </span>
                <div className="font-bold text-slate-800 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-slate-400 text-base">calendar_today</span>
                  {task.generationDate}
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">{t("mltBioburdenInitiate.taskDetails.generationDateHint")}</span>
              </div>

              {/* Assigned Operator */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.assignedOperator")}
                </span>
                <div className="font-bold text-slate-800 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-slate-400 text-base">person</span>
                  {task.assignedOperator}
                </div>
                <span className="text-[10px] text-emerald-600 mt-0.5 block font-medium">{t("mltBioburdenInitiate.taskDetails.assignedOperatorHint")}</span>
              </div>

              {/* Production Schedule ID */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.productionScheduleId")}
                </span>
                <div className="font-mono font-bold text-slate-800 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-slate-400 text-base">event_note</span>
                  {task.productionSchedule || t("mltBioburdenInitiate.taskDetails.notScheduled")}
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">{task.scheduleLine}</span>
              </div>

              {/* Required Plate Count */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.requiredPlateCount")}
                </span>
                <div className="font-bold text-slate-900 text-sm mt-1 flex items-center gap-1.5">
                  <span className="material-symbols-outlined text-indigo-500 text-base">adjust</span>
                  {t("mltBioburdenInitiate.taskDetails.platesPreAllocated", { plates: testMethod === 'Plate Count' ? 4 : 2 })}
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">{t("mltBioburdenInitiate.taskDetails.requiredPlateCountHint")}</span>
              </div>

              {/* Task Status */}
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-200/80">
                <span className="text-[10px] font-black text-slate-400 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.taskDetails.taskStatus")}
                </span>
                <div className="font-bold text-amber-700 text-sm mt-1 flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-amber-500"></span>
                  {isSubmitted ? t("mltBioburdenInitiate.taskDetails.statusInitiated") : t("mltBioburdenInitiate.taskDetails.statusPending")}
                </div>
                <span className="text-[10px] text-slate-500 mt-0.5 block font-medium">
                  {isSubmitted ? t("mltBioburdenInitiate.taskDetails.lockedForTesting") : t("mltBioburdenInitiate.taskDetails.awaitingVerification")}
                </span>
              </div>
            </div>

            {/* Traceability Compliance Note Banner */}
            <div className="mt-4 p-3.5 bg-blue-50/70 border border-blue-200/80 rounded-xl flex items-start gap-3">
              <span className="material-symbols-outlined text-blue-600 text-lg shrink-0 mt-0.5">
                verified_user
              </span>
              <p className="text-xs text-blue-900 leading-relaxed font-medium">
                <Trans i18nKey="mltBioburdenInitiate.taskDetails.traceabilityNote" components={{ strong: <strong /> }} />
              </p>
            </div>
          </div>
        </section>

        {/* SECTION 2: Sample Details Section */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0F2942] text-white px-6 py-3.5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-md bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-black border border-blue-400/30">
                2
              </span>
              <h2 className="text-xs font-black tracking-widest uppercase">
                {t("mltBioburdenInitiate.sampleDetails.title")}
              </h2>
            </div>
            <span className="text-[11px] font-bold text-slate-300">
              {t("mltBioburdenInitiate.sampleDetails.mandatoryNote")}
            </span>
          </div>

          <div className="p-6 space-y-6">
            {/* Common Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* Sample Type */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center justify-between">
                  <span>{t("mltBioburdenInitiate.sampleDetails.sampleType")}</span>
                  <span className="text-[10px] font-normal text-slate-400">{t("mltBioburdenInitiate.sampleDetails.mandatory")}</span>
                </label>
                <select
                  value={sampleType}
                  onChange={(e) => handleSampleTypeChange(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                >
                  {rawMaterialType.map((type, index) => (
                    <option key={index} value={type?.id}>
                      {type?.material_type}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.sampleDetails.sampleTypeHint")}</p>
              </div>

              {/* Sample Subtype */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center justify-between">
                  <span>{t("mltBioburdenInitiate.sampleDetails.sampleSubtype")}</span>
                  <span className="text-[10px] font-normal text-slate-400">{t("mltBioburdenInitiate.sampleDetails.dependentDropdown")}</span>
                </label>
                <select
                  value={sampleSubtype}
                  onChange={(e) => setSampleSubtype(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                >
                  {rawMaterialSub.map((type, index) => (
                    <option key={index} value={type?.id}>
                      {type?.material_type}
                    </option>
                  ))}
                </select>
                <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.sampleDetails.subtypeHint", { sampleType: sampleTypeName })}</p>
              </div>

              {/* Sample ID */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center justify-between">
                  <span>{t("mltBioburdenInitiate.sampleDetails.sampleId")}</span>
                  <button
                    type="button"
                    onClick={() => {
                      const prefix = isFinishedProduct ? 'FP' : isRawMaterial ? 'RM' : 'IP';
                      setSampleId(`SMP-2026-${prefix}-${Math.floor(1000 + Math.random() * 9000)}`);
                    }}
                    className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                  >
                    <span className="material-symbols-outlined text-xs">autorenew</span>
                    {t("mltBioburdenInitiate.sampleDetails.regenerate")}
                  </button>
                </label>
                <input
                  type="text"
                  value={sampleId}
                  onChange={(e) => setSampleId(e.target.value)}
                  placeholder={t("mltBioburdenInitiate.sampleDetails.sampleIdPlaceholder")}
                  className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                />
                <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.sampleDetails.sampleIdHint")}</p>
              </div>

              {/* Sample Quantity & UOM */}
              {/* <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center justify-between">
                  <span>{t("mltBioburdenInitiate.sampleDetails.quantityUnit")}</span>
                  <span className="text-[10px] font-normal text-slate-400">{t("mltBioburdenInitiate.sampleDetails.quantityRule")}</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="0.1"
                    min="0.1"
                    value={sampleQuantity}
                    onChange={(e) => setSampleQuantity(e.target.value)}
                    placeholder="10.0"
                    className="flex-1 bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                  />
                  <select
                    value={uom}
                    onChange={(e) => setUom(e.target.value)}
                    className="w-24 bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-2.5 py-2.5 text-xs font-bold text-slate-700 outline-none transition-all shadow-sm"
                  >
                    <option value="mL">mL</option>
                    <option value="g">g</option>
                    <option value="mg">mg</option>
                    <option value="L">L</option>
                    <option value="vials">vials</option>
                  </select>
                </div>
                <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.sampleDetails.quantityHint")}</p>
              </div> */}

              {/* Sampled Date and Time */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center justify-between">
                  <span>{t("mltBioburdenInitiate.sampleDetails.sampledDateTime")}</span>
                  <span className="text-[10px] font-normal text-slate-400">{t("mltBioburdenInitiate.sampleDetails.workflowRequired")}</span>
                </label>
                <input
                  type="datetime-local"
                  value={sampledDateTime}
                  onChange={(e) => setSampledDateTime(e.target.value)}
                  className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                />
                <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.sampleDetails.sampledDateTimeHint")}</p>
              </div>

              {/* Production Schedule context if applicable */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                  {t("mltBioburdenInitiate.sampleDetails.scheduleMapping")}
                </label>
                <div className="bg-slate-100 border border-slate-200 rounded-lg px-3.5 py-2 text-xs font-medium text-slate-700 flex items-center justify-between">
                  <span className="font-mono font-bold text-slate-800">
                    {task.productionSchedule || t("mltBioburdenInitiate.sampleDetails.notMapped")}
                  </span>
                  <span className="text-[10px] font-bold text-blue-600 bg-blue-50 px-2 py-0.5 rounded border border-blue-200">
                    {task.scheduleLine}
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.sampleDetails.scheduleMappingHint")}</p>
              </div>
            
            </div>

            {/* SAMPLE DETAILS: QUANTITY ACCOUNTING & BREAKDOWN */}
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-5 space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200/80 pb-3">
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-blue-600 text-lg">scale</span>
                  <h3 className="text-xs font-black uppercase tracking-wider text-slate-800">
                    Sample Quantities &amp; Material Accounting
                  </h3>
                </div>
                <div className="flex items-center gap-2">
                  <label className="text-[10px] font-black uppercase text-slate-500">Unit of Measure (UOM):</label>
                  <select
                    value={uom}
                    onChange={(e) => setUom(e.target.value)}
                    className="bg-white border border-slate-300 rounded-lg px-2.5 py-1 text-xs font-bold text-slate-700 outline-none shadow-sm"
                  >
                    <option value="g">g (Grams)</option>
                    <option value="mL">mL (Milliliters)</option>
                    <option value="mg">mg (Milligrams)</option>
                    <option value="kg">kg (Kilograms)</option>
                    <option value="vials">vials</option>
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
                {/* Quantity Sampled */}
                <div className="space-y-1.5 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                  <label className="text-[10px] font-black text-slate-600 uppercase tracking-wider block">
                    Quantity Sampled *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={quantitySampled}
                      onChange={(e) => setQuantitySampled(e.target.value)}
                      placeholder="100"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3 py-2 text-xs font-mono font-bold text-slate-800 outline-none pr-8"
                    />
                    <span className="absolute right-2.5 top-2 text-xs font-bold text-slate-400">{uom}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium leading-tight">
                    Total quantity received by the laboratory
                  </p>
                </div>

                {/* Quantity Used for Test */}
                <div className="space-y-1.5 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                  <label className="text-[10px] font-black text-slate-600 uppercase tracking-wider block">
                    Quantity Used for Test *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={quantityUsedForTest}
                      onChange={(e) => setQuantityUsedForTest(e.target.value)}
                      placeholder="10"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3 py-2 text-xs font-mono font-bold text-slate-800 outline-none pr-8"
                    />
                    <span className="absolute right-2.5 top-2 text-xs font-bold text-slate-400">{uom}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium leading-tight">
                    Quantity consumed for testing
                  </p>
                </div>

                {/* Retention Sample */}
                <div className="space-y-1.5 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                  <label className="text-[10px] font-black text-slate-600 uppercase tracking-wider block">
                    Retention Sample *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={retentionSample}
                      onChange={(e) => setRetentionSample(e.target.value)}
                      placeholder="20"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3 py-2 text-xs font-mono font-bold text-slate-800 outline-none pr-8"
                    />
                    <span className="absolute right-2.5 top-2 text-xs font-bold text-slate-400">{uom}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium leading-tight">
                    Quantity kept aside for future reference or re-testing
                  </p>
                </div>

                {/* Quantity Destroyed */}
                <div className="space-y-1.5 bg-white p-3.5 rounded-xl border border-slate-200 shadow-sm">
                  <label className="text-[10px] font-black text-slate-600 uppercase tracking-wider block">
                    Quantity Destroyed *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step="any"
                      min="0"
                      value={quantityDestroyed}
                      onChange={(e) => setQuantityDestroyed(e.target.value)}
                      placeholder="5"
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3 py-2 text-xs font-mono font-bold text-slate-800 outline-none pr-8"
                    />
                    <span className="absolute right-2.5 top-2 text-xs font-bold text-slate-400">{uom}</span>
                  </div>
                  <p className="text-[10px] text-slate-500 font-medium leading-tight">
                    Quantity destroyed or disposed of
                  </p>
                </div>

                {/* Remaining Quantity (Autocalculated) */}
                <div className={`space-y-1.5 p-3.5 rounded-xl border shadow-sm ${parseFloat(remainingQuantity) < 0
                  ? 'bg-rose-50 border-rose-300 text-rose-900'
                  : 'bg-blue-50/80 border-blue-200 text-blue-950'
                  }`}>
                  <label className="text-[10px] font-black uppercase tracking-wider flex items-center justify-between">
                    <span>Remaining Quantity</span>
                    <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-blue-200/60 text-blue-800 uppercase">
                      Auto
                    </span>
                  </label>
                  <div className="relative">
                    <div className="w-full bg-white border border-blue-200 rounded-lg px-3 py-2 text-xs font-mono font-black text-blue-700 shadow-inner flex items-center justify-between">
                      <span className="text-sm">{remainingQuantity}</span>
                      <span className="font-bold text-slate-500 text-xs">{uom}</span>
                    </div>
                  </div>
                  <p className="text-[10px] text-slate-600 font-medium leading-tight">
                    Autocalculated - Quantity not yet accounted for through use, retention, or destruction
                  </p>
                </div>
              </div>

              {/* Real-time Material Balance / Reconciliation Formula */}
              <div className="pt-2 flex flex-wrap items-center justify-between gap-3 text-xs">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${parseFloat(remainingQuantity) >= 0 ? 'bg-emerald-500' : 'bg-rose-500 animate-pulse'}`}></span>
                  <span className="font-bold text-slate-700">
                    Accountability Formula:
                  </span>
                  <span className="font-mono text-slate-600 text-[11px]">
                    Remaining ({remainingQuantity} {uom}) = Sampled ({quantitySampled} {uom}) - [Used ({quantityUsedForTest} {uom}) + Retention ({retentionSample} {uom}) + Destroyed ({quantityDestroyed} {uom})]
                  </span>
                </div>
                {parseFloat(remainingQuantity) < 0 ? (
                  <span className="text-xs font-bold text-rose-600 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">warning</span>
                    Accounted exceeds Sampled by {Math.abs(parseFloat(remainingQuantity))} {uom}!
                  </span>
                ) : (
                  <span className="text-xs font-bold text-emerald-700 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">check_circle</span>
                    100% Quantity Reconciled
                  </span>
                )}
              </div>
            </div>

            {/* CONDITIONAL SUBSECTION A: Finished Product */}
            {isFinishedProduct && (
              <div className="p-4 bg-gradient-to-r from-blue-50/80 to-slate-50 rounded-xl border border-blue-200 space-y-3">
                <div className="flex items-center justify-between border-b border-blue-200/60 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-blue-700 text-lg">inventory_2</span>
                    <h3 className="text-xs font-black text-blue-950 uppercase tracking-wider">
                      {t("mltBioburdenInitiate.finishedProduct.title")}
                    </h3>
                  </div>
                  <span className="text-[10px] font-bold text-blue-700 bg-blue-100 px-2 py-0.5 rounded">
                    {t("mltBioburdenInitiate.finishedProduct.mapped", { schedule: task.productionSchedule })}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Read-Only Product Name */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-500 uppercase tracking-wider flex items-center gap-1">
                      <span>{t("mltBioburdenInitiate.finishedProduct.productName")}</span>
                      <span className="material-symbols-outlined text-xs text-slate-400">lock</span>
                    </label>
                    <div className="bg-white border border-slate-300 rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 flex items-center justify-between shadow-inner">
                      <span>{task.productName}</span>
                      <span className="font-mono text-[11px] text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded">
                        {task.productCode}
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-400 italic">
                      {t("mltBioburdenInitiate.finishedProduct.populatedFrom", { schedule: task.productionSchedule })}
                    </p>
                  </div>

                  {/* Free Text Production Batch Number */}
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-slate-700 uppercase tracking-wider flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <span>{t("mltBioburdenInitiate.finishedProduct.batchNo")}</span>
                        <span className="text-[9px] font-bold text-blue-700 bg-blue-100/90 px-1.5 py-0.5 rounded uppercase">
                          {t("mltBioburdenInitiate.finishedProduct.freeText")}
                        </span>
                      </span>
                      {task.batchNo && productionBatchNo !== task.batchNo && (
                        <button
                          type="button"
                          onClick={() => setProductionBatchNo(task.batchNo)}
                          className="text-[10px] font-bold text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                          title={t("mltBioburdenInitiate.finishedProduct.resetTitle")}
                        >
                          <span className="material-symbols-outlined text-xs">restart_alt</span>
                          {t("mltBioburdenInitiate.finishedProduct.reset")}
                        </button>
                      )}
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        value={productionBatchNo}
                        onChange={(e) => setProductionBatchNo(e.target.value)}
                        placeholder={t("mltBioburdenInitiate.finishedProduct.batchNoPlaceholder")}
                        className="w-full bg-white border border-slate-300 focus:border-blue-600 focus:ring-1 focus:ring-blue-500 rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm pr-9"
                      />
                      <span className="material-symbols-outlined absolute right-3 top-2.5 text-slate-400 text-base pointer-events-none">
                        edit
                      </span>
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">
                      {t("mltBioburdenInitiate.finishedProduct.batchNoHint")}
                    </p>
                  </div>
                </div>

                {!task.productionSchedule && (
                  <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-center gap-2 text-rose-800 text-xs font-semibold">
                    <span className="material-symbols-outlined text-rose-600">error</span>
                    {t("mltBioburdenInitiate.finishedProduct.noScheduleError")}
                  </div>
                )}
              </div>
            )}

            {/* CONDITIONAL SUBSECTION B: Raw Material (Operator Direct Entry, No Inventory Lookup) */}
            {isRawMaterial && (
              <div className="p-4 bg-amber-50/70 rounded-xl border border-amber-200/90 space-y-3">
                <div className="flex items-center justify-between border-b border-amber-200/80 pb-2">
                  <div className="flex items-center gap-2">
                    <span className="material-symbols-outlined text-amber-700 text-lg">edit_note</span>
                    <h3 className="text-xs font-black text-amber-950 uppercase tracking-wider">
                      {t("mltBioburdenInitiate.rawMaterial.title")}
                    </h3>
                  </div>
                  <span className="text-[10px] font-bold text-amber-800 bg-amber-100 px-2 py-0.5 rounded">
                    {t("mltBioburdenInitiate.rawMaterial.directEntry")}
                  </span>
                </div>

                <div className="p-2.5 bg-amber-100/50 rounded-lg text-xs text-amber-900 font-medium flex items-center gap-2">
                  <span className="material-symbols-outlined text-amber-700 text-base">info</span>
                  <span>
                    {t("mltBioburdenInitiate.rawMaterial.info")}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-1">
                  {/* Material Name (Mandatory Text Entry) */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-700 uppercase tracking-wider flex items-center justify-between">
                      <span>{t("mltBioburdenInitiate.rawMaterial.materialName")}</span>
                      <span className="text-[10px] font-normal text-amber-800">{t("mltBioburdenInitiate.rawMaterial.mandatoryTextField")}</span>
                    </label>
                    <input
                      type="text"
                      value={materialName}
                      onChange={(e) => setMaterialName(e.target.value)}
                      placeholder={t("mltBioburdenInitiate.rawMaterial.materialNamePlaceholder")}
                      className="w-full bg-white border border-amber-300 focus:border-amber-600 rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    />
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.rawMaterial.materialNameHint")}</p>
                  </div>

                  {/* Material Batch/Lot Number (Mandatory Text Entry) */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-700 uppercase tracking-wider flex items-center justify-between">
                      <span>{t("mltBioburdenInitiate.rawMaterial.batchLot")}</span>
                      <span className="text-[10px] font-normal text-amber-800">{t("mltBioburdenInitiate.rawMaterial.mandatoryTextField")}</span>
                    </label>
                    <input
                      type="text"
                      value={materialBatchLot}
                      onChange={(e) => setMaterialBatchLot(e.target.value)}
                      placeholder={t("mltBioburdenInitiate.rawMaterial.batchLotPlaceholder")}
                      className="w-full bg-white border border-amber-300 focus:border-amber-600 rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                    />
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.rawMaterial.batchLotHint")}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Optional Remarks Text Area */}
            <div className="space-y-1.5">
              <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider flex items-center justify-between">
                <span>{t("mltBioburdenInitiate.remarks.label")}</span>
                <span className="text-[10px] font-normal text-slate-400">{t("mltBioburdenInitiate.remarks.charCount", { length: remarks.length })}</span>
              </label>
              <textarea
                value={remarks}
                onChange={(e) => setRemarks(e.target.value.slice(0, 500))}
                rows={2}
                placeholder={t("mltBioburdenInitiate.remarks.placeholder")}
                className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg p-3 text-xs font-medium text-slate-700 outline-none transition-all shadow-sm resize-none"
              />
            </div>
          </div>
        </section>

        {/* SECTION 3: Plate Assignment Section */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0F2942] text-white px-6 py-3.5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-md bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-black border border-blue-400/30">
                3
              </span>
              <h2 className="text-xs font-black tracking-widest uppercase">
                {t("mltBioburdenInitiate.plateAssignment.title")}
              </h2>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-[11px] font-bold text-emerald-400 flex items-center gap-1">
                <span className="material-symbols-outlined text-sm">check_circle</span>
                {t("mltBioburdenInitiate.plateAssignment.allVerified", { total: preAssignedPlates.length })}
              </span>
            </div>
          </div>

          <div className="p-6 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 text-xs text-slate-600">
              <p className="font-medium">
                {t("mltBioburdenInitiate.plateAssignment.description", { method: testMethod })}
              </p>
              <span className="text-[11px] font-black text-blue-700 bg-blue-50 px-2.5 py-1 rounded border border-blue-200">
                {t("mltBioburdenInitiate.plateAssignment.methodRequirement")} {testMethod === 'Plate Count' ? t("mltBioburdenInitiate.plateAssignment.methodRequirementPlateCount") : t("mltBioburdenInitiate.plateAssignment.methodRequirementFiltration")}
              </span>
            </div>

            {/* Pre-Assigned Plates Table */}
            <div className="overflow-x-auto border border-slate-200 rounded-xl shadow-sm">
              <table className="w-full text-left border-collapse text-xs">
                <thead>
                  <tr className="bg-slate-100 text-slate-700 font-black text-[10px] uppercase tracking-wider border-b border-slate-200">
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.index")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.serialNo")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.plateId")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.mediaType")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.plateSize")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.lotNo")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.expiryDate")}</th>
                    <th className="py-3 px-4">{t("mltBioburdenInitiate.plateTable.intendedTarget")}</th>
                    <th className="py-3 px-4 text-center">{t("mltBioburdenInitiate.plateTable.status")}</th>
                    <th className="py-3 px-4 text-center">{t("mltBioburdenInitiate.plateTable.validation")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {preAssignedPlates.map((plate, index) => (
                    <tr key={plate.serialNo} className="hover:bg-blue-50/40 transition-colors">
                      <td className="py-3.5 px-4 font-mono font-bold text-slate-400">
                        {index + 1}
                      </td>
                      <td className="py-3.5 px-4">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-slate-400 text-base">barcode</span>
                          <span className="font-mono font-black text-slate-900 text-xs">
                            {plate.serialNo}
                          </span>
                        </div>
                      </td>
                      <td className="py-3.5 px-4 font-mono text-slate-600 font-bold">
                        {plate.plateId}
                      </td>
                      <td className="py-3.5 px-4 font-bold text-slate-800">
                        {plate.mediaType}
                      </td>
                      <td className="py-3.5 px-4 text-slate-600">
                        {plate.plateSize}
                      </td>
                      <td className="py-3.5 px-4 font-mono font-bold text-indigo-700">
                        {plate.lotNo}
                      </td>
                      <td className="py-3.5 px-4">
                        <span className="font-medium text-slate-700">{plate.expiryDate}</span>
                        <span className="text-[10px] text-emerald-600 block font-semibold">{t("mltBioburdenInitiate.plateTable.valid")}</span>
                      </td>
                      <td className="py-3.5 px-4 font-medium text-slate-700">
                        <span className="px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-[11px] font-semibold">
                          {plate.intendedTarget}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className="px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                          {plate.status}
                        </span>
                      </td>
                      <td className="py-3.5 px-4 text-center">
                        <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-600">
                          <span className="material-symbols-outlined text-sm">verified</span>
                          {t("mltBioburdenInitiate.plateTable.ok")}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Validation Rules Indicator */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-lg flex items-center gap-2.5 text-xs text-emerald-900 font-medium">
                <span className="material-symbols-outlined text-emerald-600">check_circle</span>
                <span>{t("mltBioburdenInitiate.plateAssignment.allAvailable", { total: preAssignedPlates.length })}</span>
              </div>
              <div className="p-3 bg-emerald-50/70 border border-emerald-200 rounded-lg flex items-center gap-2.5 text-xs text-emerald-900 font-medium">
                <span className="material-symbols-outlined text-emerald-600">verified</span>
                <span>{t("mltBioburdenInitiate.plateAssignment.noDuplicates")}</span>
              </div>
              <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-lg flex items-center gap-2.5 text-xs text-blue-900 font-medium">
                <span className="material-symbols-outlined text-blue-600">science</span>
                <span>{t("mltBioburdenInitiate.plateAssignment.mediaCompatibility", { method: testMethod })}</span>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 4: Method-Specific Details */}
        <section className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-[#0F2942] text-white px-6 py-3.5 flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-2.5">
              <span className="w-6 h-6 rounded-md bg-blue-500/20 text-blue-300 flex items-center justify-center text-xs font-black border border-blue-400/30">
                4
              </span>
              <h2 className="text-xs font-black tracking-widest uppercase">
                {t("mltBioburdenInitiate.methodParams.title", { method: testMethod })}
              </h2>
            </div>
            <span className="text-[11px] font-mono text-blue-300">
              {t("mltBioburdenInitiate.methodParams.sopReference")}
            </span>
          </div>

          <div className="p-6">
            {/* SUB-VIEW 4A: Plate Count Specific Details */}
            {testMethod === 'Plate Count' && (
              <div className="space-y-6">
                <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl flex items-center gap-2.5 text-xs text-blue-900 font-medium">
                  <span className="material-symbols-outlined text-blue-600">info</span>
                  <span>
                    {t("mltBioburdenInitiate.plateCountParams.info")}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  {/* Pretreatment / Sample Preparation */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.plateCountParams.pretreatment")}
                    </label>
                    <select
                      value={pcPretreatment}
                      onChange={(e) => setPcPretreatment(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {preTreatmentList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.pretreatment_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.plateCountParams.pretreatmentHint")}</p>
                  </div>

                  {/* Dilution Required */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.plateCountParams.dilutionRequired")}
                    </label>
                    <div className="flex gap-2">
                      {(['Yes', 'No'] as const).map((choice) => (
                        <button
                          key={choice}
                          type="button"
                          onClick={() => setPcDilutionRequired(choice)}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all border ${pcDilutionRequired === choice
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-slate-50 text-slate-600 border-slate-300 hover:bg-slate-100'
                            }`}
                        >
                          {choice}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.plateCountParams.dilutionRequiredHint")}</p>
                  </div>

                  {/* Dilution Factor */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.plateCountParams.dilutionFactor")} {pcDilutionRequired === 'Yes' && '*'}
                    </label>
                    <select
                      value={pcDilutionFactor}
                      onChange={(e) => setPcDilutionFactor(e.target.value)}
                      disabled={pcDilutionRequired === 'No'}
                      className={`w-full border rounded-lg px-3.5 py-2.5 text-xs font-bold outline-none transition-all shadow-sm ${pcDilutionRequired === 'No'
                          ? 'bg-slate-100 border-slate-200 text-slate-400 cursor-not-allowed'
                          : 'bg-slate-50 border-slate-300 focus:border-blue-600 focus:bg-white text-slate-800'
                        }`}
                    >
                      {dilutionfactorList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.df}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.plateCountParams.dilutionFactorHint")}</p>
                  </div>

                  {/* Neutralizer */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.plateCountParams.neutralizer")}
                    </label>
                    <select
                      value={pcNeutralizer}
                      onChange={(e) => setPcNeutralizer(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {neutralizerList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.solution_name}
                        </option>
                      ))}                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.plateCountParams.neutralizerHint")}</p>
                  </div>

                  {/* Inoculation Type */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.plateCountParams.inoculationType")}
                    </label>
                    <div className="flex gap-2">
                      {['Pour Plate Method', 'Spread Plate Method'].map((type) => (
                        <button
                          key={type}
                          type="button"
                          onClick={() => setPcInoculationType(type)}
                          className={`flex-1 py-2.5 rounded-lg text-xs font-black uppercase tracking-wider transition-all border ${pcInoculationType === type
                              ? 'bg-blue-600 text-white border-blue-600 shadow-sm'
                              : 'bg-slate-50 text-slate-600 border-slate-300 hover:bg-slate-100'
                            }`}
                        >
                          {type}
                        </button>
                      ))}
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.plateCountParams.inoculationTypeHint")}</p>
                  </div>

                  {/* Volume Plated & UOM */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.plateCountParams.volumePlated")}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="0.1"
                        min="0.1"
                        value={pcVolumePlated}
                        onChange={(e) => setPcVolumePlated(e.target.value)}
                        placeholder="1.0"
                        className="flex-1 bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                      />
                      <select
                        value={pcUom}
                        onChange={(e) => setPcUom(e.target.value)}
                        className="w-24 bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-2.5 py-2.5 text-xs font-bold text-slate-700 outline-none transition-all shadow-sm"
                      >
                        <option value="mL">mL</option>
                      </select>
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.plateCountParams.volumePlatedHint")}</p>
                  </div>
                </div>

                {/* Approved Incubation Conditions Reference (Read-only) */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm text-blue-600">thermostat</span>
                    {t("mltBioburdenInitiate.methodParams.incubationTitle")}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-600">
                    <div className="p-2.5 bg-white border border-slate-200 rounded-lg">
                      <strong className="text-slate-800 block">{t("mltBioburdenInitiate.plateCountParams.tamcTitle")}</strong>
                      <Trans i18nKey="mltBioburdenInitiate.plateCountParams.tamcText" components={{ strong: <strong /> }} />
                    </div>
                    <div className="p-2.5 bg-white border border-slate-200 rounded-lg">
                      <strong className="text-slate-800 block">{t("mltBioburdenInitiate.plateCountParams.tymcTitle")}</strong>
                      <Trans i18nKey="mltBioburdenInitiate.plateCountParams.tymcText" components={{ strong: <strong /> }} />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* SUB-VIEW 4B: Filtration Specific Details */}
            {testMethod === 'Filtration' && (
              <div className="space-y-6">
                <div className="p-3 bg-blue-50/70 border border-blue-200 rounded-xl flex items-center gap-2.5 text-xs text-blue-900 font-medium">
                  <span className="material-symbols-outlined text-blue-600">info</span>
                  <span>
                    {t("mltBioburdenInitiate.filtrationParams.info")}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                  {/* Sample Preparation / Pretreatment */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.pretreatment")}
                    </label>
                    <select
                      value={filtPretreatment}
                      onChange={(e) => setFiltPretreatment(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {preTreatmentList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.pretreatment_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.pretreatmentHint")}</p>
                  </div>

                  {/* Filter Type */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.filterType")}
                    </label>
                    <select
                      value={filtType}
                      onChange={(e) => setFiltType(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {filterTypeList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.filter_type_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.filterTypeHint")}</p>
                  </div>

                  {/* Pore Size */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.poreSize")}
                    </label>
                    <select
                      value={filtPoreSize}
                      onChange={(e) => setFiltPoreSize(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {poreSizeList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.pore_size}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.poreSizeHint")}</p>
                  </div>

                  {/* Membrane Diameter */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.membraneDiameter")}
                    </label>
                    <select
                      value={filtMembraneDiameter}
                      onChange={(e) => setFiltMembraneDiameter(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {membraneDiameterList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.membrane_diameter}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.membraneDiameterHint")}</p>
                  </div>

                  {/* Volume Filtered & UOM */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.volumeFiltered")}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="1"
                        min="1"
                        value={filtVolumeFiltered}
                        onChange={(e) => setFiltVolumeFiltered(e.target.value)}
                        placeholder="100.0"
                        className="flex-1 bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                      />
                      <select
                        value={filtUom}
                        onChange={(e) => setFiltUom(e.target.value)}
                        className="w-24 bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-2.5 py-2.5 text-xs font-bold text-slate-700 outline-none transition-all shadow-sm"
                      >
                        <option value="mL">mL</option>
                        <option value="L">L</option>
                      </select>
                    </div>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.volumeFilteredHint")}</p>
                  </div>

                  {/* Number of Filters / Membranes */}
                  <div className="space-y-1.5">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.numFilters")}
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="10"
                      value={filtNumFilters}
                      onChange={(e) => setFiltNumFilters(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-mono font-bold text-slate-800 outline-none transition-all shadow-sm"
                    />
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.numFiltersHint")}</p>
                  </div>

                  {/* Neutralizer / Rinse Fluid */}
                  <div className="space-y-1.5 md:col-span-2">
                    <label className="text-[11px] font-black text-slate-600 uppercase tracking-wider block">
                      {t("mltBioburdenInitiate.filtrationParams.neutralizer")}
                    </label>
                    <select
                      value={filtNeutralizer}
                      onChange={(e) => setFiltNeutralizer(e.target.value)}
                      className="w-full bg-slate-50 border border-slate-300 focus:border-blue-600 focus:bg-white rounded-lg px-3.5 py-2.5 text-xs font-bold text-slate-800 outline-none transition-all shadow-sm"
                    >
                      {neutralizerList.map((type, index) => (
                        <option key={index} value={type?.id}>
                          {type?.solution_name}
                        </option>
                      ))}
                    </select>
                    <p className="text-[10px] text-slate-500 font-medium">{t("mltBioburdenInitiate.filtrationParams.neutralizerHint")}</p>
                  </div>
                </div>

                {/* Approved Incubation Conditions Reference (Read-only) */}
                <div className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                  <h4 className="text-xs font-black text-slate-700 uppercase tracking-wider flex items-center gap-1.5">
                    <span className="material-symbols-outlined text-sm text-blue-600">thermostat</span>
                    {t("mltBioburdenInitiate.methodParams.incubationTitle")}
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-slate-600">
                    <div className="p-2.5 bg-white border border-slate-200 rounded-lg">
                      <strong className="text-slate-800 block">{t("mltBioburdenInitiate.filtrationParams.membrane1Title")}</strong>
                      <Trans i18nKey="mltBioburdenInitiate.filtrationParams.membrane1Text" components={{ strong: <strong /> }} />
                    </div>
                    <div className="p-2.5 bg-white border border-slate-200 rounded-lg">
                      <strong className="text-slate-800 block">{t("mltBioburdenInitiate.filtrationParams.membrane2Title")}</strong>
                      <Trans i18nKey="mltBioburdenInitiate.filtrationParams.membrane2Text" components={{ strong: <strong /> }} />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* SECTION 5: Footer Actions & Sticky Control Bar */}
        <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-md flex flex-wrap items-center justify-between gap-4 sticky bottom-4 z-20">
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-full ${isFormValid ? 'bg-emerald-500' : 'bg-amber-500'}`}></span>
              <span className="text-xs font-bold text-slate-700">
                {isFormValid ? t("mltBioburdenInitiate.footerBar.allValid") : t("mltBioburdenInitiate.footerBar.incomplete", { total: validationErrors.length })}
              </span>
            </div>
            <span className="text-slate-300">|</span>
            <span className="text-xs text-slate-500 hidden sm:inline">
              {t("mltBioburdenInitiate.footerBar.operator")} <strong className="text-slate-700">{task.assignedOperator}</strong>
            </span>
          </div>

          <div className="flex items-center gap-3">
            {/* Cancel Action */}
            <button
              type="button"
              onClick={onBack}
              className="px-6 py-2.5 rounded-xl border border-slate-300 text-xs font-black text-slate-600 uppercase tracking-widest hover:bg-slate-100 transition-all active:scale-95"
            >
              {t("mltBioburdenInitiate.common.cancel")}
            </button>

            {/* Initiate MLT Test Action */}
            <button
              type="button"
              onClick={handleInitiateClick}
              disabled={isSubmitted || isSubmitting}
              className={`px-8 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest transition-all shadow-md flex items-center gap-2 ${isSubmitted
                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed shadow-none'
                  : isFormValid
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/25 active:scale-95'
                    : 'bg-blue-600 hover:bg-blue-700 text-white shadow-blue-500/25 active:scale-95'
                }`}
            >
              <span className="material-symbols-outlined text-base">play_arrow</span>
              {t("mltBioburdenInitiate.footerBar.initiate")}
            </button>
          </div>
        </div>
      </main>

      <EsignModal 
        show={showEsignAlert}
        onHide={() => setShowEsignAlert(false)}
        onConfirm={handleConfirmInitiation}
        needRemraks={false}
        screenName={t("mltBioburdenInitiate.header.title")}
      />

    </div>
  );
};

export default MLTBioburdenInitiate;